import configuration from '../configuration';
import BaseAPI from './BaseAPI';

/**
 * One node of a filter tree sent to analytics-service: either a group node (op + clauses) or a
 * leaf node (field + operator + values). The service validates field/operator against its own
 * whitelist (senderAccountId, recipientAccountId, hasAttachment, messageType; EQ, IN) — org scoping
 * is never expressible here, since the service always applies it itself. senderAccountId/
 * recipientAccountId each match against both a user account id and a role account id server-side,
 * so a single id from the typeahead endpoint (searchUsersAndRoles) works whether it's a user or role.
 */
export type ArchivingFilterNode = {
  op?: 'AND' | 'OR';
  clauses?: ArchivingFilterNode[];
  field?: 'senderAccountId' | 'recipientAccountId' | 'hasAttachment' | 'messageType';
  operator?: 'EQ' | 'IN';
  values?: string[];
};

export type ArchivingMessageSearchRequest = {
  orgId: string;
  query: string;
  startDate?: string;
  endDate?: string;
  hasAttachment?: boolean;
  senderAccountIds?: string[];
  recipientAccountIds?: string[];
  filter?: ArchivingFilterNode;
  /** Results per page, max 1000. */
  limit?: number;
  columns?: string[];
  /** Opaque cursor from a previous response's nextToken; only valid with the same filters. */
  nextToken?: string;
};

/**
 * Known fields on a search result row — kept optional/loose beyond this since the exact backend
 * column set is still a placeholder pending data eng confirmation (see
 * analytics-service/docs/ARCHIVING_MESSAGE_SEARCH.md's "Known placeholders" notes).
 * senderAccountId/recipientAccountId are the raw account tokens (recipientAccountId is null for a
 * group/DL result — see analytics-service's DISTINCT dedupe); senderDisplayName/
 * recipientDisplayName are the resolved names (ACCOUNTS.DISPLAYNAME, falling back to
 * "First Last", falling back to the raw id) — "Role Name (User Name)" when a message has both a
 * role and a user sender/recipient. recipientDisplayName is null for group/DL, same as
 * recipientAccountId. channelId is null for a p2p result, populated for group/DL.
 */
export type ArchivingMessageSearchResult = {
  messageId?: string;
  sentAt?: string;
  senderAccountId?: string | null;
  senderDisplayName?: string;
  recipientAccountId?: string | null;
  recipientDisplayName?: string | null;
  body?: string;
  hasAttachment?: boolean;
  /** Raw MESSAGES.MESSAGETYPE value (e.g. P2P, GROUP, DIST, PUBLIC_GROUP, DIST_GROUP), or null when unset. */
  messageType?: string | null;
  conversationId?: string;
  channelId?: string | null;
};

export type ArchivingMessageSearchData = {
  results: ArchivingMessageSearchResult[];
  /** Rows in this page only — there is no true total across pages. */
  resultCount: number;
  /** Pass back as nextToken for the next page. Null on the last page. */
  nextToken?: string | null;
};

export type ArchivingMessageSearchMetadata = {
  processingTimeMs?: number;
  snowflakeAccount?: string;
};

/** status/requestId/timestamp envelope shared by every Archiving response type. */
export type ArchivingResponseEnvelope = {
  status: string;
  requestId: string;
  timestamp: string;
};

export type ArchivingMessageSearchResponse = ArchivingResponseEnvelope & {
  data: ArchivingMessageSearchData;
  metadata?: ArchivingMessageSearchMetadata;
};

export type ArchivingConversationRequest = {
  orgId: string;
  conversationId: string;
  /** Messages per page. Defaults to 25 server-side. */
  limit?: number;
  /** Opaque cursor from a previous response's nextToken. */
  nextToken?: string;
};

/**
 * Known fields on a conversation message — same placeholder caveat as ArchivingMessageSearchResult.
 * No recipientAccountId/recipientDisplayName here (unlike search): the conversation endpoint omits
 * them entirely rather than nulling them, since this view shows the thread rather than who
 * received what. senderDisplayName resolution matches ArchivingMessageSearchResult's.
 */
export type ArchivingConversationMessage = {
  messageId?: string;
  sentAt?: string;
  senderAccountId?: string | null;
  senderDisplayName?: string;
  body?: string;
  hasAttachment?: boolean;
  conversationId?: string;
  channelId?: string | null;
};

export type ArchivingConversationData = {
  messages: ArchivingConversationMessage[];
  /** Pass back as nextToken for the next page. Null on the last page. */
  nextToken?: string | null;
};

export type ArchivingConversationResponse = ArchivingResponseEnvelope & {
  data: ArchivingConversationData;
  metadata?: ArchivingMessageSearchMetadata;
};

export type ArchivingConversationMetadataRequest = {
  orgId: string;
  conversationId: string;
};

export type ArchivingConversationMetadataData = {
  conversationId: string;
  channelId: string | null;
  messageCount: number;
};

export type ArchivingConversationMetadataResponse = ArchivingResponseEnvelope & {
  data: ArchivingConversationMetadataData;
  metadata?: ArchivingMessageSearchMetadata;
};

export type ArchivingExportRequest = {
  orgId: string;
  exportName?: string;
  /**
   * Message identifiers to export. The caller resolves all product use cases (selected messages, a
   * full conversation thread, a saved search) into this one list before calling startExport, so the
   * export always reflects exactly what the user reviewed rather than a live re-query at export time.
   */
  messageIds: string[];
  includeAttachments?: boolean;
  notes?: string;
};

export type ArchivingExportData = {
  exportJobId: string;
  /** Job lifecycle status. Always 'queued' for now — no job processing exists yet. */
  jobStatus: string;
};

export type ArchivingExportResponse = ArchivingResponseEnvelope & {
  data: ArchivingExportData;
};

export type ArchivingExportListRequest = {
  orgId: string;
  /** Exports per page, 1-100. Defaults to 25 server-side. */
  pageSize?: number;
  /** Opaque cursor from a previous response's nextToken. */
  nextToken?: string;
};

export type ArchivingExportSummary = {
  exportJobId: string;
  exportName?: string;
  /** 'queued' | 'processing' | 'ready' | 'failed' */
  jobStatus: string;
  createdAt: string;
  /**
   * When this job's TTL semantics say it should be considered gone — recomputed on every status
   * transition server-side (queued/processing/ready/failed each have their own window). Check this
   * before treating a 'ready' job as downloadable; analytics-service's download endpoint enforces
   * it too, but the UI shouldn't rely solely on that round trip to decide what to show as available.
   */
  expiresAt: string;
};

export type ArchivingExportListData = {
  /** The caller's own export jobs, most recent first — never every export in the org (an archiving_admin all-exports view is planned but not built). */
  exports: ArchivingExportSummary[];
  /** Pass back as nextToken for the next page. Null on the last page. */
  nextToken?: string | null;
};

export type ArchivingExportListResponse = ArchivingResponseEnvelope & {
  data: ArchivingExportListData;
};

export type ArchivingExportDownloadRequest = {
  orgId: string;
  jobId: string;
};

export type ArchivingExportDownloadData = {
  downloadUrl: string;
  /** When this presigned URL itself expires (15 minutes from issuance) — not the export job's own expiresAt. */
  urlExpiresAt: string;
};

export type ArchivingExportDownloadResponse = ArchivingResponseEnvelope & {
  data: ArchivingExportDownloadData;
};

export type ArchivingMessageAttachmentRequest = {
  orgId: string;
  messageId: string;
};

/**
 * Unlike every other Archiving* response, this isn't JSON — the endpoint streams the decrypted
 * attachment bytes directly with its own mimeType as Content-Type (analytics-service's
 * de-attachment-open-api-design.md). blob.type carries that mimeType.
 */
export type ArchivingMessageAttachmentResponse = {
  blob: Blob;
  url: string;
};

export type ArchivingTypeaheadRequest = {
  orgId: string;
  /** Name text to search for, matched anywhere in the name (minimum 2 characters). */
  query: string;
  limit?: number;
};

export type ArchivingTypeaheadResult = {
  /** User or role account token — the same id accepted by ArchivingFilterNode's senderAccountId/recipientAccountId fields. */
  id: string;
  displayName: string;
  type: 'user' | 'role';
};

export type ArchivingTypeaheadData = {
  results: ArchivingTypeaheadResult[];
};

export type ArchivingTypeaheadResponse = ArchivingResponseEnvelope & {
  data: ArchivingTypeaheadData;
  metadata?: ArchivingMessageSearchMetadata;
};

/**
 * One selected user/role row from a saved search's filters. Same shape as ArchivingTypeaheadResult
 * plus which selector (sender/recipient) the row was entered under — persisted verbatim so a saved
 * search restores its user/role rows without a follow-up typeahead lookup.
 */
export type ArchivingSavedSearchUserRole = {
  id: string;
  displayName: string;
  type: 'user' | 'role';
  role: 'sender' | 'recipient' | 'either';
};

/** The full Message Search filter state, as persisted by a saved search. */
export type ArchivingSavedSearchFilters = {
  query?: string;
  /** 'last24h' | 'last7d' | 'last30d' | 'thisQuarter' | 'custom' */
  timeRangePreset?: string;
  /** Only meaningful when timeRangePreset is 'custom'. */
  customStartDate?: string;
  /** Only meaningful when timeRangePreset is 'custom'. */
  customEndDate?: string;
  userRoles?: ArchivingSavedSearchUserRole[];
  /** 'any' | 'all' — how multiple userRoles rows are combined (OR vs AND). Only meaningful with 2+ rows. */
  userRoleMatchMode?: 'any' | 'all';
  messageType?: string | null;
  patientReference?: string | null;
  hasAttachment?: boolean | null;
};

export type ArchivingCreateSavedSearchRequest = {
  orgId: string;
  name: string;
  filters: ArchivingSavedSearchFilters;
};

export type ArchivingSavedSearchData = {
  searchId: string;
  name: string;
  filters: ArchivingSavedSearchFilters;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
};

export type ArchivingSavedSearchResponse = ArchivingResponseEnvelope & {
  data: ArchivingSavedSearchData;
};

export type ArchivingSavedSearchListRequest = {
  orgId: string;
  /** When true, scopes to the caller's own saved searches. Defaults to false (every saved search in the org) server-side. */
  mine?: boolean;
};

export type ArchivingSavedSearchListData = {
  /** Saved searches, most recently created first. */
  savedSearches: ArchivingSavedSearchData[];
};

export type ArchivingSavedSearchListResponse = ArchivingResponseEnvelope & {
  data: ArchivingSavedSearchListData;
};

export type ArchivingDeleteSavedSearchRequest = {
  orgId: string;
  searchId: string;
};

/** No data payload on a successful delete — just the shared envelope. */
export type ArchivingDeleteSavedSearchResponse = ArchivingResponseEnvelope;

/**
 * Audit log events, from analytics-service's archiving audit log (design doc 0006). Message-returning
 * events say "returned", not "viewed": the service knows what it sent back, not what the UI rendered.
 * The list isn't closed — the service can add event types — so any string is accepted too.
 */
export type ArchivingAuditEventType =
  | 'search_performed'
  | 'messages_returned'
  | 'conversation_metadata_returned'
  | 'attachment_returned'
  | 'export_requested'
  | 'export_completed'
  | 'export_failed'
  | 'export_downloaded'
  | 'export_history_listed'
  | 'saved_search_created'
  | 'saved_search_listed'
  | 'saved_search_deleted'
  | 'audit_log_read'
  | 'access_denied'
  | (string & Record<never, never>);

export type ArchivingAuditOutcome = 'success' | 'failure' | 'denied';

export type ArchivingAuditLogListRequest = {
  orgId: string;
  /** Only events performed by this account token. */
  actorAccountToken?: string;
  eventType?: ArchivingAuditEventType;
  outcome?: ArchivingAuditOutcome;
  /** Only events involving this message, conversation, export job, saved search, or attachment id. */
  resourceId?: string;
  /** Earliest event time, inclusive (ISO 8601). */
  from?: string;
  /** Latest event time, inclusive (ISO 8601). Must not be earlier than from. */
  to?: string;
  /** Events per page, 1-200. Defaults to 50 server-side. */
  pageSize?: number;
  /** Opaque cursor from a previous response's nextToken; only valid for the same org and actor filter. */
  nextToken?: string;
};

/**
 * Filters recorded on an audit event: ids, dates, and enums only — never the search text or a
 * patient reference value (only whether one was supplied).
 */
export type ArchivingAuditFilters = {
  startDate?: string | null;
  endDate?: string | null;
  hasAttachment?: boolean | null;
  hasPatientReference?: boolean | null;
  messageType?: string | null;
  senderAccountIds?: string[] | null;
  recipientAccountIds?: string[] | null;
  /** On audit_log_read events: the filters the admin applied when reading the log. */
  auditLogActor?: string | null;
  auditLogEventType?: string | null;
  auditLogOutcome?: string | null;
  auditLogResourceId?: string | null;
};

/** One audit event. Metadata only — never message content. */
export type ArchivingAuditLogEntry = {
  eventId: string;
  timestamp: string;
  eventType: ArchivingAuditEventType;
  outcome: ArchivingAuditOutcome;
  httpStatus?: number | null;
  actorAccountToken: string;
  /** Roles held in the requested org at event time; product-level roles are prefixed 'product:'. */
  actorRoles: string[];
  requestId?: string | null;
  sourceIp?: string | null;
  userAgent?: string | null;
  /** Message ids returned or exported by the event. */
  messageIds: string[];
  conversationId?: string | null;
  jobId?: string | null;
  searchId?: string | null;
  attachmentId?: string | null;
  resultCount?: number | null;
  /** Length of the free-text query; the query text itself is never recorded. */
  queryLength?: number | null;
  filters?: ArchivingAuditFilters | null;
};

export type ArchivingAuditLogListData = {
  /** Audit events, newest first. */
  events: ArchivingAuditLogEntry[];
  /**
   * Pass back as nextToken for the next page. Null on the last page. A page can be shorter than
   * pageSize (or even empty) while this is still set when non-actor filters are applied, so keep
   * paging until it's null.
   */
  nextToken?: string | null;
};

export type ArchivingAuditLogListResponse = ArchivingResponseEnvelope & {
  data: ArchivingAuditLogListData;
};

export default class ArchivingAPI extends BaseAPI {
  // analytics-service lives at api.{env}.tigerconnect.com, not the devapi base URL
  private get tcBaseUrl(): string {
    const { apiEnv } = this.config;
    return configuration.TC_URLS[apiEnv] || `https://api.${apiEnv}.tigerconnect.com`;
  }

  searchMessages = async (
    organizationId: string,
    payload: ArchivingMessageSearchRequest
  ): Promise<ArchivingMessageSearchResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/search`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingMessageSearchResponse;
  };

  getConversation = async (
    organizationId: string,
    payload: ArchivingConversationRequest
  ): Promise<ArchivingConversationResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/conversation`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingConversationResponse;
  };

  getConversationMetadata = async (
    organizationId: string,
    payload: ArchivingConversationMetadataRequest
  ): Promise<ArchivingConversationMetadataResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/conversation/metadata`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingConversationMetadataResponse;
  };

  startExport = async (
    organizationId: string,
    payload: ArchivingExportRequest
  ): Promise<ArchivingExportResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/export`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingExportResponse;
  };

  listExports = async (
    organizationId: string,
    payload: ArchivingExportListRequest
  ): Promise<ArchivingExportListResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/export`;
    const { orgId, pageSize, nextToken } = payload;
    const { data } = await this.httpClient.get(url, {
      headers,
      query: {
        orgId,
        ...(pageSize !== undefined ? { pageSize } : {}),
        ...(nextToken ? { nextToken } : {}),
      },
    });
    return data as unknown as ArchivingExportListResponse;
  };

  getExportDownloadUrl = async (
    organizationId: string,
    payload: ArchivingExportDownloadRequest
  ): Promise<ArchivingExportDownloadResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/export/${payload.jobId}/download`;
    const { data } = await this.httpClient.get(url, {
      headers,
      query: { orgId: payload.orgId },
    });
    return data as unknown as ArchivingExportDownloadResponse;
  };

  /**
   * Fetches, decrypts, and returns a single archived attachment's bytes. Unlike the other
   * Archiving* calls, this doesn't go through the JSON get/post methods — the endpoint streams
   * raw bytes, so it uses the same absolute-URL blob-download path as the messaging API's own
   * attachment downloads (downloadFileBlobAndUrlFromUrl), just pointed at analytics-service's
   * tcBaseUrl with the TT-X-Organization-Key header this API's other calls already send.
   */
  getMessageAttachment = async (
    organizationId: string,
    payload: ArchivingMessageAttachmentRequest
  ): Promise<ArchivingMessageAttachmentResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/message/${encodeURIComponent(
      payload.messageId
    )}/attachment?orgId=${encodeURIComponent(payload.orgId)}`;
    return this.httpClient.downloadFileBlobAndUrlFromUrl(url, { headers });
  };

  searchUsersAndRoles = async (
    organizationId: string,
    payload: ArchivingTypeaheadRequest
  ): Promise<ArchivingTypeaheadResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/typeahead`;
    const { data } = await this.httpClient.get(url, {
      headers,
      query: {
        orgId: payload.orgId,
        query: payload.query,
        ...(payload.limit !== undefined ? { limit: payload.limit } : {}),
      },
    });
    return data as unknown as ArchivingTypeaheadResponse;
  };

  createSavedSearch = async (
    organizationId: string,
    payload: ArchivingCreateSavedSearchRequest
  ): Promise<ArchivingSavedSearchResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/messages/saved-searches`;
    const { data } = await this.httpClient.post(url, {
      headers,
      data: payload,
    });
    return data as unknown as ArchivingSavedSearchResponse;
  };

  getSavedSearches = async (
    organizationId: string,
    payload: ArchivingSavedSearchListRequest
  ): Promise<ArchivingSavedSearchListResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/saved-searches`;
    const { data } = await this.httpClient.get(url, {
      headers,
      query: {
        orgId: payload.orgId,
        ...(payload.mine !== undefined ? { mine: payload.mine } : {}),
      },
    });
    return data as unknown as ArchivingSavedSearchListResponse;
  };

  deleteSavedSearch = async (
    organizationId: string,
    payload: ArchivingDeleteSavedSearchRequest
  ): Promise<ArchivingDeleteSavedSearchResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/saved-searches/${payload.searchId}`;
    const { data } = await this.httpClient.del(url, {
      headers,
      query: { orgId: payload.orgId },
    });
    return data as unknown as ArchivingDeleteSavedSearchResponse;
  };

  /**
   * Reads one org's archiving audit log, newest first. Requires both view_archiving and
   * archiving_admin in the org (403 otherwise).
   * Only filters that were actually provided are sent.
   */
  getAuditLog = async (
    organizationId: string,
    payload: ArchivingAuditLogListRequest
  ): Promise<ArchivingAuditLogListResponse> => {
    const headers = { 'TT-X-Organization-Key': organizationId };
    const url = `${this.tcBaseUrl}/v1/analytics-service/archiving/audit-log`;
    const {
      orgId,
      actorAccountToken,
      eventType,
      outcome,
      resourceId,
      from,
      to,
      pageSize,
      nextToken,
    } = payload;
    const { data } = await this.httpClient.get(url, {
      headers,
      query: {
        orgId,
        ...(actorAccountToken ? { actorAccountToken } : {}),
        ...(eventType ? { eventType } : {}),
        ...(outcome ? { outcome } : {}),
        ...(resourceId ? { resourceId } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(pageSize !== undefined ? { pageSize } : {}),
        ...(nextToken ? { nextToken } : {}),
      },
    });
    return data as unknown as ArchivingAuditLogListResponse;
  };
}
